import { format } from 'date-fns';
import { CallActions } from 'src/hooks/getCallActions';
import { CallsGroupedByHourPST } from 'src/hooks/useCallsGroupedByHourPST';
import { CallsGroupedByStage } from 'src/hooks/useCallsGroupedByStage';
import { CallsGroupedByStageOutcome } from 'src/hooks/useCallsGroupedByStageOutcome';
import { CallsGroupedByStatus } from 'src/hooks/useCallsGroupedByStatus';
import { PerDayUsage } from 'src/interfaces/usage.interface';

const colors = [
  '#6715FF',
  '#109E77',
  '#ECAB2F',
  '#2E86C1',
  '#8E44AD',
  '#16A085',
  '#D35400',
  '#7F8C8D',
  '#C71540',
  '#FF6B6B',
];

export const prepareCallsGroupedByStatusGraphData = (
  callsGroupedByStatusData: CallsGroupedByStatus[],
) => {
  const groupedByDate = callsGroupedByStatusData.reduce(
    (acc, item) => {
      const date = format(new Date(item.executionDate), 'MMM d');

      if (!acc[date]) {
        acc[date] = {};
      }

      acc[date][item.status] = item.count;
      return acc;
    },
    {} as Record<string, Record<string, number>>,
  );

  const labels = Object.keys(groupedByDate);

  const statusMapping = {
    did_not_connect: 'Did not connect',
    unanswered: 'Unanswered',
    voice_mail: 'Voicemail',
    error: 'Error',
    ended: 'Ended',
    in_progress: 'In progress',
    not_started: 'Not started',
  };

  const datasets = Object.entries(statusMapping)
    .map(([status, label], index) => ({
      label,
      data: labels.map((date) => groupedByDate[date][status] || 0),
      backgroundColor: colors[index % colors.length],
    }))
    .filter((dataset) => dataset.data.some((value) => value > 0));

  return {
    labels,
    datasets,
  };
};

export const prepareCallsGroupedByStageOutcomeGraphData = (
  callsGroupedByStageOutcomeData: CallsGroupedByStageOutcome[],
) => {
  const groupedByDate = callsGroupedByStageOutcomeData.reduce(
    (acc, item) => {
      const date = format(new Date(item.executionDate), 'MMM d');

      if (!acc[date]) {
        acc[date] = {};
      }

      acc[date][item.stageOutcome] = item.count;
      return acc;
    },
    {} as Record<string, Record<string, number>>,
  );

  const labels = Object.keys(groupedByDate);

  const statusMapping = {
    human_disconnected: 'Human',
    bot_disconnected: 'Bot',
  };

  const datasets = Object.entries(statusMapping)
    .map(([status, label], index) => ({
      label,
      data: labels.map((date) => groupedByDate[date][status] || 0),
      backgroundColor: colors[index % colors.length],
    }))
    .filter((dataset) => dataset.data.some((value) => value > 0));

  return {
    labels,
    datasets,
  };
};

export const prepareCallsGroupedByStageGraphData = (
  callsGroupedByStageData: CallsGroupedByStage[],
) => {
  const groupedByDate = callsGroupedByStageData.reduce(
    (acc, item) => {
      const date = format(new Date(item.executionDate), 'MMM d');

      if (!acc[date]) {
        acc[date] = {};
      }

      acc[date][item.stage] = item.count;
      return acc;
    },
    {} as Record<string, Record<string, number>>,
  );

  const labels = Object.keys(groupedByDate);

  const statusMapping = {
    transfer_completed: 'Transfer Completed',
    transfer_started: 'Transfer Started',
    picked_up: 'Picked Up',
    created: 'Created',
  };

  const datasets = Object.entries(statusMapping)
    .map(([status, label], index) => ({
      label,
      data: labels.map((date) => groupedByDate[date][status] || 0),
      backgroundColor: colors[index % colors.length],
    }))
    .filter((dataset) => dataset.data.some((value) => value > 0));

  return {
    labels,
    datasets,
  };
};

export const prepareCallsGroupedByHourPSTGraphData = (
  callsGroupedByHourPSTData: CallsGroupedByHourPST[],
) => {
  const labels = [
    '12:00 AM',
    '1:00 AM',
    '2:00 AM',
    '3:00 AM',
    '4:00 AM',
    '5:00 AM',
    '6:00 AM',
    '7:00 AM',
    '8:00 AM',
    '9:00 AM',
    '10:00 AM',
    '11:00 AM',
    '12:00 PM',
    '1:00 PM',
    '2:00 PM',
    '3:00 PM',
    '4:00 PM',
    '5:00 PM',
    '6:00 PM',
    '7:00 PM',
    '8:00 PM',
    '9:00 PM',
    '10:00 PM',
    '11:00 PM',
  ];

  const hourCountMap = callsGroupedByHourPSTData.reduce(
    (acc, item) => {
      acc[item.callHour] = item.callCount;
      return acc;
    },
    {} as Record<string, number>,
  );

  const data = labels.map((hour) => hourCountMap[hour] || 0);
  const hasNonZeroValues = data.some((value) => value > 0);

  const datasets = hasNonZeroValues
    ? [
        {
          data,
          borderColor: colors[4],
          backgroundColor: colors[4],
          tension: 0.4,
        },
      ]
    : [];

  return {
    labels: hasNonZeroValues ? labels : [],
    datasets,
  };
};

export const prepareCallActionsGraphData = (callActions: CallActions[]) => {
  const nonZeroActions =
    callActions?.filter((action) => action.numCalls > 0) || [];

  return {
    labels: nonZeroActions.map((item) => item.actionName),
    datasets: [
      {
        data: nonZeroActions.map((item) => item.numCalls),
        backgroundColor: colors,
      },
    ],
  };
};

export const preparePerDayUsageGraphData = (
  perDayUsage: PerDayUsage[],
  startDate: string,
  endDate: string,
) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const differenceInDays =
    (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
  const labels = Array.from({ length: differenceInDays + 1 }, (_, i) =>
    format(new Date(start.getTime() + (i + 1) * 24 * 60 * 60 * 1000), 'MMM d'),
  );
  const minuteUsageData = labels.map((label) => {
    const usage = perDayUsage.find(
      (item) => format(new Date(item.end_time * 1000), 'MMM d') === label,
    );
    if (!usage) {
      return 0;
    }
    return usage.aggregated_value / 60; // convert to minutes
  });
  const datasets = [
    {
      label: 'Usage (minutes)',
      data: minuteUsageData,
      backgroundColor: colors[0],
    },
  ];
  return {
    labels,
    datasets,
  };
};
